import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowTopRightOnSquareIcon,
  CalendarIcon,
  MapPinIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import { getAuthToken } from '../../utils/authToken';

const KIND_LABELS = {
  appel_offre: "Appel d'offres",
  bourse: 'Bourse',
  subvention: 'Subvention',
  formation: 'Formation',
  concours: 'Concours',
  financement: 'Financement',
  partenariat: 'Partenariat',
  emploi: 'Emploi'
};

const TYPE_LABELS = {
  financement: 'Financement',
  formation: 'Formation',
  concours: 'Concours',
  partenariat: 'Partenariat',
  emploi: 'Emploi'
};

const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const linesToList = (t) => t.split('\n').map((l) => l.trim()).filter(Boolean);
// Conditions / avantages : normalement une liste, parfois un simple texte (collecte automatique)
const listToLines = (v) => (Array.isArray(v) ? v.join('\n') : typeof v === 'string' ? v : '');

const authHeaders = async () => ({ Authorization: `Bearer ${await getAuthToken()}` });

const CfpDrafts = () => {
  const queryClient = useQueryClient();

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});

  const { data: drafts = [], isLoading, isError, refetch } = useQuery(
    'admin-cfp-drafts',
    async () => {
      const response = await axios.get('/api/admin/cfp-drafts', { headers: await authHeaders() });
      return response.data.drafts || [];
    }
  );

  const decide = useMutation(
    async ({ id, action }) => {
      await axios.post(`/api/admin/cfp-drafts/${id}/${action}`, {}, { headers: await authHeaders() });
      return action;
    },
    {
      onSuccess: (action) => {
        toast.success(action === 'approve' ? 'Appel publié' : 'Appel rejeté');
        queryClient.invalidateQueries('admin-cfp-drafts');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "L'opération a échoué");
        queryClient.invalidateQueries('admin-cfp-drafts');
      }
    }
  );

  const save = useMutation(
    async ({ id, payload }) => {
      await axios.patch(`/api/admin/cfp-drafts/${id}`, payload, { headers: await authHeaders() });
    },
    {
      onSuccess: () => {
        toast.success('Brouillon modifié');
        setEditingId(null);
        queryClient.invalidateQueries('admin-cfp-drafts');
      },
      onError: (error) => toast.error(error.response?.data?.message || 'Modification impossible')
    }
  );

  const startEdit = (d) => {
    setEditingId(d.id);
    setForm({
      title: d.title || '',
      description: d.description || '',
      organisme: d.organisme || '',
      kind: d.kind,
      type: d.type,
      category: d.category || '',
      budget: d.budget ?? 0,
      currency: d.currency || 'EUR',
      deadline: toDateInput(d.deadline),
      location: d.location || '',
      website: d.website || '',
      contact_email: d.contact_email || '',
      requirements: listToLines(d.requirements),
      benefits: listToLines(d.benefits)
    });
  };

  const submitEdit = (e) => {
    e.preventDefault();
    save.mutate({
      id: editingId,
      payload: {
        ...form,
        budget: Number(form.budget) || 0,
        deadline: `${form.deadline}T23:59:59Z`,
        requirements: linesToList(form.requirements),
        benefits: linesToList(form.benefits)
      }
    });
  };

  const field = 'w-full border rounded px-3 py-2 text-sm';
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (isLoading) return <p className="text-gray-600">Chargement…</p>;
  if (isError) {
    return (
      <div className="text-gray-700">
        Impossible de charger les brouillons.{' '}
        <button type="button" className="text-primary underline" onClick={() => refetch()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-text">Appels à valider</h2>
        <p className="text-gray-600 text-sm">
          Appels collectés automatiquement, avec lien officiel. Rien n'est publié tant que vous n'avez pas approuvé.
        </p>
      </div>

      {drafts.length === 0 && (
        <div className="bg-white rounded-lg border p-6 text-gray-600">Aucun appel en attente.</div>
      )}

      {drafts.map((d) => (editingId === d.id ? (
        <form key={d.id} onSubmit={submitEdit} className="bg-white rounded-lg border p-5 space-y-3">
          <input className={field} value={form.title} onChange={set('title')} placeholder="Titre" required />
          <textarea className={field} rows={6} value={form.description} onChange={set('description')} placeholder="Description" required />
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={field} value={form.organisme} onChange={set('organisme')} placeholder="Organisme" />
            <input className={field} value={form.category} onChange={set('category')} placeholder="Catégorie" />
            <select className={field} value={form.kind} onChange={set('kind')}>
              {Object.entries(KIND_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select className={field} value={form.type} onChange={set('type')}>
              {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <input className={field} type="date" value={form.deadline} onChange={set('deadline')} required />
            <input className={field} value={form.location} onChange={set('location')} placeholder="Lieu" />
            <input className={field} type="number" min="0" value={form.budget} onChange={set('budget')} placeholder="Budget" />
            <input className={field} value={form.currency} onChange={set('currency')} placeholder="Devise" />
            <input className={field} value={form.website} onChange={set('website')} placeholder="Site web (https://…)" />
            <input className={field} type="email" value={form.contact_email} onChange={set('contact_email')} placeholder="E-mail de contact" />
          </div>
          <textarea className={field} rows={3} value={form.requirements} onChange={set('requirements')} placeholder="Conditions (une par ligne)" />
          <textarea className={field} rows={3} value={form.benefits} onChange={set('benefits')} placeholder="Avantages (un par ligne)" />
          <div className="flex gap-3">
            <button type="submit" disabled={save.isLoading} className="px-4 py-2 rounded bg-primary text-white text-sm disabled:opacity-50">Enregistrer</button>
            <button type="button" onClick={() => setEditingId(null)} className="px-4 py-2 rounded border text-sm">Annuler</button>
          </div>
        </form>
      ) : (
        <div key={d.id} className="bg-white rounded-lg border p-5 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">{KIND_LABELS[d.kind] || d.kind}</span>
            {d.organisme && <span className="text-gray-600">{d.organisme}</span>}
          </div>
          <h3 className="text-lg font-semibold text-text">{d.title}</h3>
          <p className="text-gray-700 text-sm whitespace-pre-line">{d.description}</p>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1">
              <CalendarIcon className="h-4 w-4" />
              Date limite : {new Date(d.deadline).toLocaleDateString('fr-FR')}
            </span>
            {d.location && (
              <span className="inline-flex items-center gap-1">
                <MapPinIcon className="h-4 w-4" />
                {d.location}
              </span>
            )}
            <a
              href={d.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Source officielle
            </a>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              disabled={decide.isLoading}
              onClick={() => decide.mutate({ id: d.id, action: 'approve' })}
              className="inline-flex items-center gap-1 px-4 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-50"
            >
              <CheckCircleIcon className="h-4 w-4" />
              Publier
            </button>
            <button
              type="button"
              onClick={() => startEdit(d)}
              className="inline-flex items-center gap-1 px-4 py-2 rounded border text-sm"
            >
              <PencilSquareIcon className="h-4 w-4" />
              Modifier
            </button>
            <button
              type="button"
              disabled={decide.isLoading}
              onClick={() => decide.mutate({ id: d.id, action: 'reject' })}
              className="inline-flex items-center gap-1 px-4 py-2 rounded border border-red-300 text-red-700 text-sm disabled:opacity-50"
            >
              <XCircleIcon className="h-4 w-4" />
              Rejeter
            </button>
          </div>
        </div>
      )))}
    </div>
  );
};

export default CfpDrafts;
